import { useCallback, useEffect, useState } from 'react';

import { uniqBy } from 'lodash';
import { PlusIcon } from 'lucide-react';
import { Button, Select, DefaultTooltip } from 'twyne-new.ui';

import { useCraftProp } from '@/components/builder/utils';
import { useFontFamily, useResponsive, useUtilities } from '@/modules/cms/views/post/components/builder/context';

import { useFontConfig } from '../utils/tailwindConfig/hooks';

export default function FontFamilyProperty() {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  const { value: fontFamily, setValue: setFontFamily } = useCraftProp('fontFamily')
  const { value: fontSerif, setValue: setFontSerif } = useCraftProp('fontSerif')
  const { value: fontConfig, setValue: setFontConfig } = useFontConfig()
  const { loadFonts } = useFontFamily()

  const [serif, setSerif] = useState()

  useEffect(() => {
    setSerif()
  }, [device, selectedUtilities])

  useEffect(() => {
    if (serif === undefined) {
      setSerif(!!fontSerif)
    }
  }, [fontSerif, serif])

  const handleChange = option => {
    const { value, url } = option || {}
    setFontFamily(value)
    if (value && url) {
      setFontConfig({
        ...fontConfig || {},
        [value]: url,
      })
    }
  }

  const loadOptions = useCallback(async value => {
    const options = await loadFonts(value)

    return { options: uniqBy(options, 'value') }
  }, [loadFonts])

  return <div className="flex gap-1">
    <Select
      variant="accent"
      value={getValue(fontFamily)}
      onChange={handleChange}
      loadOptions={loadOptions}
      placeholder='Font Family'
      clearable
      async
      asyncOnType
      defaultOptions={DEFAULT_FONTS}
    />

    {serif ? <>
      <Select
        variant="accent"
        value={fontSerif}
        onChange={option => setFontSerif(option?.value)}
        options={SERIF_OPTIONS}
        placeholder='Font Fallback'
        clearable
      />
    </> : <>
      <DefaultTooltip content='Add Serif' triggerProps={{ asChild: true }}>
        <Button variant='light' onClick={() => setSerif(true)}><PlusIcon size='16' /></Button>
      </DefaultTooltip>
    </>}

  </div>
}

const SERIF_OPTIONS = [
  { value: "serif", label: "Serif" },
  { value: "sans-serif", label: "Sans Serif" },
  { value: "monospace", label: "Monospace" },
  { value: "cursive", label: "Cursive" },
  { value: "fantasy", label: "Fantasy" },
  { value: "system-ui", label: "System UI" },
]

const DEFAULT_FONTS = [
  { label: 'Arial', value: 'Arial', url: '' },
  { label: 'Helvetica', value: 'Helvetica', url: '' },
  { label: 'Verdana', value: 'Verdana', url: '' },
  { label: 'Tahoma', value: 'Tahoma', url: '' },
  { label: 'Trebuchet MS', value: 'Trebuchet_MS', url: '' },

  { label: 'Times New Roman', value: 'Times_New_Roman', url: '' },
  { label: 'Georgia', value: 'Georgia', url: '' },
  { label: 'Garamond', value: 'Garamond', url: '' },
  { label: 'Palatino Linotype', value: 'Palatino_Linotype', url: '' },
  { label: 'Book Antiqua', value: 'Book_Antiqua', url: '' },

  { label: 'Courier New', value: 'Courier_New', url: '' },
  { label: 'Lucida Console', value: 'Lucida_Console', url: '' },
  { label: 'Monaco', value: 'Monaco', url: '' },
  {
    label: 'Roboto',
    value: 'Roboto',
    url: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap',
  },
  {
    label: 'Montserrat',
    value: 'Montserrat',
    url: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap',
  },
  {
    label: 'Open Sans',
    value: 'Open_Sans',
    url: 'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap',
  },
  {
    label: 'Raleway',
    value: 'Raleway',
    url: 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100..900;1,100..900&display=swap',
  },
  {
    label: 'Inter',
    value: 'Inter',
    url: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
  {
    label: 'Bebas Neue',
    value: 'Bebas_Neue',
    url: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap',
  },
  {
    label: 'Short Stack',
    value: 'Short_Stack',
    url: 'https://fonts.googleapis.com/css2?family=Short+Stack&display=swap',
  },
  {
    label: 'Poppins',
    value: 'Poppins',
    url: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  },
]

function getValue(font) {
  if (!font) return;
  return { value: font, label: font.replace(/_/g, ' ') }
}
